interface ProfessionalTermsPageProps {
  onBack: () => void
}

const COMPETITION_POLICY = [
  'Match data, player statistics, and related performance records must be linked to a recognized, verifiable competition, tournament, or cup.',
  'Friendly matches, training games, exhibitions, scrimmages, and other unofficial fixtures may not be recorded on Scouta.',
  'Scouta may reject or remove competition entries that are not tied to an officially recognized competition.',
]

const TERMS_SECTIONS = [
  {
    title: '1. Acceptance of Terms',
    paragraphs: [
      'These Terms of Service govern your access to and use of Scouta, including the website, dashboards, player database, reports, scouting tools, communication flows, and related services.',
      'By using Scouta, creating an account, uploading content, or accessing player data, you agree to these terms.',
    ],
  },
  {
    title: '2. What Scouta Is',
    bullets: [
      'Scouta is a football data, scouting, workflow, and discovery platform.',
      'Scouta helps users organize player profiles, match records, reports, team data, and public discovery pages.',
      'Scouta is not a football governing body, legal adviser, or guaranteed transfer intermediary.',
      'Scouta does not promise contracts, trials, transfers, recruitment outcomes, or commercial deals.',
    ],
  },
  {
    title: '3. Accounts and Eligibility',
    bullets: [
      'You must provide accurate registration information and keep it updated.',
      'You are responsible for activity that occurs through your account.',
      'You must protect your login credentials and notify Scouta if you suspect unauthorized access.',
      'If you act on behalf of a club, academy, agency, or other organization, you represent that you are authorized to do so.',
    ],
  },
  {
    title: '4. User Roles',
    paragraphs: [
      'Scouta may provide different permissions to Scouts, Clubs, administrators, and other user types. Access to features may vary by role, verification status, moderation settings, or administrative review.',
      'Scouta may grant, restrict, suspend, or revoke role permissions when needed for safety, compliance, abuse prevention, or operations.',
    ],
  },
  {
    title: '5. Content You Upload',
    bullets: [
      'You retain responsibility for the text, images, videos, notes, stats, player details, match logs, and other content you submit.',
      'You must only upload content you have the right to use, share, store, and publish.',
      'You must not upload false, defamatory, unlawful, abusive, infringing, exploitative, or misleading content.',
      'If you publish a player profile or related information, you are responsible for ensuring that publication is permitted.',
    ],
  },
  {
    title: '6. Public and Private Player Profiles',
    paragraphs: [
      'Scouta allows certain player records to be marked public or private. Public profiles may appear on Explore, Top Talents, shareable links, and similar discovery surfaces.',
      'Private settings are intended to reduce visibility, but Scouta administrators may still access records for support, moderation, security, compliance, and operational reasons.',
      'Administrators may adjust visibility settings where reasonably necessary to operate, protect, or moderate the platform.',
    ],
  },
  {
    title: '7. Competition Data Standards',
    bullets: COMPETITION_POLICY,
  },
  {
    title: '8. Acceptable Use',
    bullets: [
      'Use Scouta for lawful football, scouting, analytics, recruitment, club, or operational purposes.',
      'Do not impersonate another person or organization.',
      'Do not harass, exploit, threaten, or deceive players or other users.',
      'Do not scrape, bulk export, repackage, resell, or systematically harvest platform data without permission.',
      'Do not interfere with platform security, attempt unauthorized access, reverse engineer protected areas, or use automated abuse tools.',
      'Do not use Scouta in a way that violates employment laws, youth protection rules, privacy laws, football regulations, or intellectual property rights.',
    ],
  },
  {
    title: '9. Data Accuracy and Independent Judgment',
    paragraphs: [
      'Scouta may host player information supplied by users, Clubs, Scouts, and administrators. We do not guarantee that every profile, stat, note, or report is complete, current, or accurate.',
      'You are responsible for your own due diligence before making scouting, hiring, contracting, transfer, or commercial decisions based on information found on Scouta.',
    ],
  },
  {
    title: '10. Intellectual Property',
    paragraphs: [
      'Scouta and its branding, interface, software, and original platform materials are owned by Scouta or its licensors and are protected by applicable intellectual property laws.',
      'By uploading content, you grant Scouta a non-exclusive license to host, store, reproduce, display, process, and distribute that content as needed to operate and improve the service, enforce these terms, and provide platform functionality.',
    ],
  },
  {
    title: '11. Suspension and Termination',
    bullets: [
      'Scouta may suspend, restrict, or terminate accounts that violate these terms or create legal, reputational, operational, or safety risks.',
      'Scouta may remove content, disable public access, or limit features while investigating abuse, fraud, ownership disputes, compliance concerns, or platform misuse.',
      'You may stop using Scouta at any time, but certain records may remain in backups, audit logs, or retained systems where reasonably necessary.',
    ],
  },
  {
    title: '12. Disclaimers',
    paragraphs: [
      'Scouta is provided on an as-is and as-available basis. To the fullest extent permitted by law, Scouta disclaims warranties of merchantability, fitness for a particular purpose, non-infringement, uninterrupted availability, and guaranteed accuracy.',
      'Scouta does not guarantee that the platform will always be secure, error free, or continuously available.',
    ],
  },
  {
    title: '13. Limitation of Liability',
    paragraphs: [
      'To the fullest extent permitted by law, Scouta will not be liable for indirect, incidental, special, consequential, exemplary, or punitive damages, or for loss of profits, business, reputation, contracts, opportunities, or data arising from your use of the service.',
      'Scouta is also not responsible for decisions, contacts, negotiations, contracts, transfers, recruitment outcomes, or disputes that occur between users or outside the platform.',
    ],
  },
  {
    title: '14. Indemnity',
    paragraphs: [
      'You agree to defend, indemnify, and hold harmless Scouta and its operators from claims, liabilities, losses, damages, and expenses arising out of your content, your use of the service, your breach of these terms, or your violation of another person\'s rights.',
    ],
  },
  {
    title: '15. Changes to the Service and Terms',
    paragraphs: [
      'Scouta may update, suspend, or discontinue parts of the service at any time.',
      'Scouta may also revise these Terms of Service. Continued use after updated terms take effect means you accept the revised terms.',
    ],
  },
]

export default function ProfessionalTermsPage({ onBack }: ProfessionalTermsPageProps) {
  return (
    <div className="min-h-screen bg-[#050910] text-white">
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-[-10%] left-[-10%] w-[30%] h-[30%] bg-green-500/10 blur-[120px] rounded-full" />
          <div className="absolute bottom-[-10%] right-[-10%] w-[30%] h-[30%] bg-blue-500/10 blur-[120px] rounded-full" />
        </div>

        <div className="relative max-w-5xl mx-auto px-4 py-20">
          <div className="space-y-6">
            <button
              onClick={onBack}
              className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.2em] text-green-400 hover:text-green-300 transition-colors"
            >
              Back to Scouta
            </button>

            <section className="space-y-4">
              <p className="text-xs uppercase tracking-[0.35em] text-slate-400">Legal</p>
              <h1 className="text-4xl md:text-5xl font-display font-bold">Terms of Service</h1>
              <p className="text-sm uppercase tracking-[0.22em] text-slate-400">Effective Date: May 22, 2026</p>
              <p className="text-lg text-slate-200 max-w-3xl leading-relaxed">
                These terms describe the rules for using Scouta as a public visitor, Scout, Club, administrator,
                or other platform user.
              </p>
            </section>
          </div>

          <div className="mt-12 rounded-3xl border border-green-500/20 bg-green-500/5 p-6 text-slate-100 leading-relaxed">
            <p>
              This page is product-ready content for the app, but terms should still be reviewed by legal counsel
              before being treated as final legal language for a live commercial service.
            </p>
          </div>

          <div className="mt-6 rounded-3xl border border-amber-400/20 bg-amber-400/10 p-6 text-slate-100 leading-relaxed">
            <h2 className="text-xl font-bold">Competition Data Policy</h2>
            <ul className="mt-4 space-y-3 text-slate-200">
              {COMPETITION_POLICY.map((item) => (
                <li key={item} className="flex gap-3">
                  <span className="mt-2 h-2 w-2 rounded-full bg-amber-300 shrink-0" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="mt-10 grid gap-6">
            {TERMS_SECTIONS.map((section) => (
              <article key={section.title} className="surface-panel rounded-3xl border border-white/10 p-6 md:p-8">
                <h2 className="text-xl md:text-2xl font-bold">{section.title}</h2>

                {section.paragraphs?.map((paragraph) => (
                  <p key={paragraph} className="mt-4 text-slate-200 leading-relaxed">
                    {paragraph}
                  </p>
                ))}

                {section.bullets ? (
                  <ul className="mt-4 space-y-3 text-slate-200 leading-relaxed">
                    {section.bullets.map((item) => (
                      <li key={item} className="flex gap-3">
                        <span className="mt-2 h-2 w-2 rounded-full bg-green-500 shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}

            <article className="surface-panel rounded-3xl border border-white/10 p-6 md:p-8">
              <h2 className="text-xl md:text-2xl font-bold">15. Contact</h2>
              <p className="mt-4 text-slate-200 leading-relaxed">
                For legal, compliance, or account questions, contact Scouta at{' '}
                <a href="mailto:scoutasupport@gmail.com" className="text-green-400 hover:text-green-300">
                  scoutasupport@gmail.com
                </a>
                .
              </p>
            </article>
          </div>
        </div>
      </div>
    </div>
  )
}
